import { FC, useCallback, useContext, useEffect, useState } from 'react';
import useRunTriggers, { Trigger } from '../hooks/useRunTriggers';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getContentType } from '../common/Common';
import { getTranslation } from '../common/Translation';
import { AppContext } from '../contexts/AppContext';
import ImageMapPopup from './ImageMapPopup';
import MapPin, { MAP_CIRCLE_ANCHOR, MAP_PIN_ANCHOR } from './MapPin';
import useFetchDocument from '../hooks/useFetchDocument';
import useFetchMedia from '../hooks/useFetchMedia';

interface ImageMapMarkerProps {
  documentId: number,
  title?: string,
  subtitle?: string,
  image?: any,
  markerPosition: { x: number, y: number },
  markerColor?: string,
  markerStyle?: string,
  callback?: () => void,
  transparent?: boolean,
  notClickable?: boolean,
  useTriggers?: boolean,
  triggers?: Trigger[]
}

const Marker = styled.div<{ $zoom?: number, $transparent?: boolean }>`
  position: absolute;
  transform: scale(0) translate3d(0, 0, 0);
  zoom: ${({ $zoom }) => $zoom};
  scale: 1;
  z-index: 10;
  cursor: pointer;
  display: inline;
  line-height: 0;

  /* Hide only the pin — the popup (a child) must stay visible */
  & > .imagemap-pin {
    display: inline;
    opacity: ${({ $transparent }) => $transparent ? 0 : 1};
  }

  &.mapmarker {
    transform-origin: ${MAP_PIN_ANCHOR.x}px ${MAP_PIN_ANCHOR.y}px;
  }

  &.circle {
    transform-origin: ${MAP_CIRCLE_ANCHOR.x}px ${MAP_CIRCLE_ANCHOR.y}px;
  }

  &:active {
    filter: brightness(0.8);
  }

  &&::before {
    content: "";
    position: absolute;
    padding: 40px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &.active {
    z-index: 13;
  }
`;

/**
 * Render a marker for the imagemap view
 * @returns {JSX.Element} Component template
 */
const ImageMapMarker: FC<ImageMapMarkerProps> = ({documentId, title, subtitle, image, markerPosition, markerColor, markerStyle = "mapmarker", callback, transparent, notClickable, useTriggers, triggers}) => {
  const runTriggers = useRunTriggers();
  const navigate = useNavigate();
  const { currentLanguage, deviceId, previewIsActive } = useContext(AppContext);
  const [presentationTitle, setPresentationTitle] = useState<any>(null);
  const [presentationSubtitle, setPresentationSubtitle] = useState<any>(null);
  const [contentType, setContentType] = useState<string>("");
  const [clickPosition, setClickPosition] = useState<any>(null);
  const [data] = useFetchDocument(documentId);
  const [presentationMedia] = useFetchMedia(image?.mediaId ? image : (data?.content?.general?.image || data?.content?.background?.image), { dimension: "600x600" });
  const isCircle = markerStyle === "circle";
  const anchorX = isCircle ? MAP_CIRCLE_ANCHOR.x : MAP_PIN_ANCHOR.x;
  const anchorY = isCircle ? MAP_CIRCLE_ANCHOR.y : MAP_PIN_ANCHOR.y;

  // Get content type of a presentation or article
  useEffect(() => {
    setContentType(getContentType(data))
  }, [data]);

  // Get title and subtitle
  useEffect(() => {
    if (data) {
      setPresentationTitle(getTranslation(title, currentLanguage)?.length > 0 ? getTranslation(title, currentLanguage) : getTranslation(data?.title, currentLanguage));
      setPresentationSubtitle(getTranslation(subtitle, currentLanguage));
    }
  }, [title, subtitle, currentLanguage, data]);

  /**
   * Show popup on first tap; follow the popup link on a second tap of the same marker.
   */
  const showPopup = useCallback((e: any) => {
    const popup = e.currentTarget.querySelector("div.imagemappopup");
    const alreadyOpen = popup?.classList?.contains("visible");

    if (alreadyOpen) {
      if (!previewIsActive && !notClickable && deviceId) {
        navigate(`/device/${deviceId}/${contentType || "article"}/${documentId}`);
      }
      return;
    }

    // Run triggers when the marker is tapped to open its popup
    runTriggers(useTriggers, triggers);

    // Hide all other popups
    const popups = document.querySelectorAll(`.imagemappopup`);
    popups?.forEach(element => {
      element.classList.remove("visible");
    });

    // Make sure all other markers are behind the current popup
    const markers = document.querySelectorAll(`.imagemapelement`);
    markers?.forEach(element => {
      element.classList.remove("active");
    });

    e.currentTarget.classList.add("active");
    popup?.classList?.add("visible");

    setClickPosition({
      x: e?.clientX,
      y: e?.clientY
    });
  }, [contentType, deviceId, documentId, navigate, notClickable, previewIsActive, runTriggers, useTriggers, triggers]);

  // Run callback function when data is loaded
  useEffect(() => {
    if (callback && data?.content) {
      callback();
    }
  }, [callback, data?.content]);

  // Do not display link if not available in current language
  if (data?.status && !(data?.title?.[currentLanguage || "no"] || data?.content?.content?.blocks?.[currentLanguage || "no"])) { return null; }

  // Only display if published
  if (data?.status && !data?.status?.includes("published")) { return null; }

  return (
    <Marker
      className={`imagemapelement ${markerStyle || "mapmarker"}`}
      style={{
        left: `calc(${markerPosition.x}% - ${anchorX}px)`,
        top: `calc(${markerPosition.y}% - ${anchorY}px)`
      }}
      onClick={showPopup}
      $transparent={Boolean(transparent)}
    >
      <span className="imagemap-pin">
        <MapPin color={markerColor || "#ffffff"} markerStyle={markerStyle || "mapmarker"} />
      </span>
      <ImageMapPopup documentId={documentId} title={presentationTitle} image={presentationMedia} position={clickPosition} contentType={contentType} markerColor={markerColor} subtitle={presentationSubtitle} notClickable={notClickable}/>
    </Marker>
  );
}

export default ImageMapMarker;
